'use client';

import { useState } from 'react';
import type { ReactNode, FC } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useCookies } from '@packages/shared/src/providers/CookieProvider/CookieProvider';
import { Link } from '@packages/modules/src/Link/Link';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Button } from '@packages/shared/src/ui/Button/Button';

import { useOriginalSearchQuery } from '../../hooks/useOriginalSearchQuery';

const messages = defineMessages({
  description: {
    id: 'search.feedback.description',
    defaultMessage:
      'Hilf uns unsere Suche zu verbessern. Klicke auf Lob oder Kritik und teile uns deine Meinung mit.',
  },
  title: {
    id: 'search.feedback.title',
    defaultMessage: 'Dein Kommentar zur Suche',
  },
  buttons: {
    id: 'search.feedback.buttons',
    defaultMessage:
      '<feedback>Lob_BAUR:Mein Lob</feedback><feedback>Kritik_BAUR:Meine Kritik</feedback>',
  },
  dialogTitle: {
    id: 'search.feedback.dialogTitle',
    defaultMessage: 'Feedback',
  },
  questionTitle: {
    id: 'search.feedback.question.title',
    defaultMessage: 'Konkrete Fragen?',
  },
  questionDescription: {
    id: 'search.feedback.question.description',
    defaultMessage:
      'Falls Sie konkrete Fragen haben und eine Antwort von uns wünschen, benutzen Sie bitte an unser <link>/service-hilfe/beratung/kontakt/:Kontaktformular</link>.',
  },
});
/**
 * Feedback button to open feedback form. The missing url part (buttonType placeholder) will be received from the locale file.
 * The format of the chunk is: <url>:<text> (e.g. Lob_BAUR:Mein Lob)
 * It is defined in the locale file as a string with the following format: <feedback><url>:<text></feedback><feedback><url>:<text></feedback>
 * @param chunk - string with url and text for the button
 * @param onClick - function to open feedback form with the fully structured url
 * */
const SearchFeedbackButton: FC<{ chunk: string; onClick: (url?: string) => void }> = ({
  chunk,
  onClick,
}) => {
  const { searchResultUserFeedback } = useConfig();

  const [url, text] = chunk.toString().split(':');
  return (
    <Button
      key={url}
      className="w-full"
      onClick={() => onClick(searchResultUserFeedback.appUrl?.replace('<buttonType>', url))}
    >
      {text}
    </Button>
  );
};

/**
 * Displays Search Feedback form to give user the possibility for some feedback
 * */
export const SearchFeedback: FC = () => {
  const [baseFeedbackUrl, setBaseFeedbackUrl] = useState<string>();
  const query = useOriginalSearchQuery();
  const { getCookies } = useCookies();
  const cookies = getCookies();

  const { formatMessage } = useIntl();

  const sid = cookies.SESSIONID;

  const feedbackIframeUrl = baseFeedbackUrl
    ?.replace('<sessionId>', sid)
    .replace('<searchQuery>', encodeURIComponent(query));

  return (
    <>
      <div className="mt-2.5 flex flex-col gap-2.5 lg:flex-row">
        <Card className="flex-2 overflow-auto">
          <CardContent className="pb-3">
            <h5 className="mb-1">
              <FormattedMessage key="search.feedback.title" {...messages.title} />
            </h5>
            <div className="-mx-2" />
            <Divider className="-mx-2 mb-2.5" />

            <div className="gap-1">
              <p className="text-size-body-sm">
                <FormattedMessage key="search.feedback.description" {...messages.description} />
              </p>
              <div className="mt-2 flex max-w-[328px] gap-1">
                <FormattedMessage
                  key="search.feedback.buttons"
                  {...messages.buttons}
                  values={{
                    feedback: (content) =>
                      content.map((chunk) => {
                        if (!chunk || typeof chunk !== 'string') return chunk;
                        return <SearchFeedbackButton chunk={chunk} onClick={setBaseFeedbackUrl} />;
                      }),
                  }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="flex-1 overflow-auto">
          <CardContent className="pb-3">
            {/*
             *  Some tenants have feedback question description but no title
             *  and therefore the headline and divider need to be conditional
             */}
            {formatMessage(messages.questionTitle) !== ' ' && (
              <>
                <h5 className="mb-1">
                  <FormattedMessage
                    key="search.feedback.question.title"
                    {...messages.questionTitle}
                  />
                </h5>
                <Divider className="-mx-2 mb-2.5" />
              </>
            )}
            <p className="text-size-body-sm">
              <FormattedMessage
                key="search.feedback.question.description"
                {...messages.questionDescription}
                values={{
                  link: (content: ReactNode[]) =>
                    content.map((c) => {
                      const [url, text] = (c as string).split(':');
                      return <Link href={url}>{text}</Link>;
                    }),
                }}
              />
            </p>
          </CardContent>
        </Card>
      </div>
      {baseFeedbackUrl && (
        <Dialog isOpen={!!baseFeedbackUrl} onClose={() => setBaseFeedbackUrl(undefined)}>
          <DialogHeader onClose={() => setBaseFeedbackUrl(undefined)}>
            {formatMessage(messages.dialogTitle)}
          </DialogHeader>
          <DialogContent>
            <iframe
              title={formatMessage(messages.dialogTitle)}
              height="100%"
              style={{ minHeight: 'calc(100vh - 15rem)', border: 0, overflow: 'auto' }}
              width="100%"
              src={feedbackIframeUrl}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};
